///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { PanelActionTooltip } from "@/components/controls/anchored-panel/PanelHeaderActions"
import { Badge, CoordFooter, formatTimeAgo, MoreSection, SectionTitle } from "@/components/controls/anchored-panel/panelAtoms"
import { WebcamStationsTable } from "@/earthbrowser/dataset-distribution/webcams/WebcamStationsTable"
import { formatDistance } from "@/earthbrowser/utils/unitFormatters"
import { setWebcamSheet } from "@/redux/dialogSlice"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch } from "@/redux/store"
import { datasetUrl } from "@/utils/datasetUrl"
import type { WebcamEntry } from "@shared-types/webcam"
import Hls from "hls.js"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

// Refresh no faster than the origin's own cadence, and never faster than
// this floor so we stay polite to source agencies even if a connector
// misreports its refresh interval
const MIN_REFRESH_MS = 30_000

// Nearby cameras: everything within this many degrees of latitude (and
// the equivalent longitude span), nearest first, capped for the strip;
// the contact sheet gets the whole set
const NEARBY_DEG = 0.35
const NEARBY_QUERY_LIMIT = 400
const NEARBY_STRIP_MAX = 24
// Cameras closer than this share a mast (FAA sites have four facing views)
const NEARBY_SAME_SITE_KM = 0.05

const categoryLabels: Record<WebcamEntry["category"], string> = {
	traffic: "Traffic",
	scenic: "Scenic",
	weather: "Weather",
	marine: "Marine",
	aviation: "Aviation",
	wildlife: "Wildlife",
}

export function AnchoredWebcamInfoPanel() {
	// Manual refresh lives here (not in the body) because the button is in
	// the shell-owned header; the body watches the nonce and re-fetches
	const [refreshNonce, setRefreshNonce] = useState(0)

	return (
		<AnchoredPanelShell
			match={data => data?.type === "webcam" ? data : null}
			fallbackTitle={content => content.webcam.name}
			contentKey={content => content.webcam.id}
			accentBorder="border-fuchsia-400/20"
			pill="bg-fuchsia-400/90"
			cardClassName={content => content.webcam.status === "stale" ? "opacity-80" : ""}
			lockMinHeight={false}
			headerExtras={() => (
				<PanelActionTooltip label="Refresh image">
					<button
						onClick={() => setRefreshNonce(n => n + 1)}
						className="pointer-events-auto w-5 h-5 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
						aria-label="Refresh image"
					>
						<svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
							<path d="M9.5 5.5a4 4 0 1 1-1.17-2.83" />
							<path d="M9.5 1v2.5H7" />
						</svg>
					</button>
				</PanelActionTooltip>
			)}
		>
			{(content, ctx) => <WebcamBody webcam={content.webcam} refreshNonce={refreshNonce} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

/**
 * HLS player: native playback where supported (Safari), hls.js elsewhere.
 * Streams pull directly from the origin (e.g. Caltrans) — never our infra.
 */
function HlsPlayer({ src }: { src: string }) {
	const videoRef = useRef<HTMLVideoElement>(null)

	useEffect(() => {
		const video = videoRef.current
		if (!video) return

		if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = src
			void video.play().catch(() => {})
			return
		}

		if (!Hls.isSupported()) return
		const hls = new Hls({ enableWorker: true })
		hls.loadSource(src)
		hls.attachMedia(video)
		hls.on(Hls.Events.MANIFEST_PARSED, () => {
			void video.play().catch(() => {})
		})
		return () => hls.destroy()
	}, [src])

	return (
		<video
			ref={videoRef}
			muted
			playsInline
			className="absolute inset-0 w-full h-full object-cover"
		/>
	)
}

/** Player selected by stream type, mounted when the user hits play */
function StreamPlayer({ webcam, tick }: { webcam: WebcamEntry, tick: number }) {
	const streamUrl = webcam.stream_url!

	switch (webcam.stream_type) {
		case "youtube": {
			const sep = streamUrl.includes("?") ? "&" : "?"
			return (
				<iframe
					src={`${streamUrl}${sep}autoplay=1&mute=1&playsinline=1`}
					allow="autoplay; encrypted-media; picture-in-picture"
					allowFullScreen
					className="absolute inset-0 w-full h-full border-0"
					title="Live webcam stream"
				/>
			)
		}
		case "hls":
			return <HlsPlayer src={streamUrl} />
		case "mp4": {
			// Short clips (e.g. TfL ~9s): loop, and re-fetch on the refresh tick
			// so a long-open panel picks up newer clips
			const sep = streamUrl.includes("?") ? "&" : "?"
			return (
				<video
					key={tick}
					src={`${streamUrl}${sep}eb=${tick}`}
					muted
					autoPlay
					loop
					playsInline
					className="absolute inset-0 w-full h-full object-cover"
				/>
			)
		}
		case "mjpeg":
			return (
				<img
					src={streamUrl}
					alt="Live webcam stream"
					className="absolute inset-0 w-full h-full object-cover"
				/>
			)
		default:
			return null
	}
}

interface NearbyWebcam {
	cam: WebcamEntry
	distanceKm: number
}

function thumbUrlFor(cam: WebcamEntry): string {
	const stamp = cam.last_success ? new Date(cam.last_success).getTime() : 0
	return `${datasetUrl(cam.thumb_url)}?eb=${stamp}`
}

function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
	const rad = Math.PI / 180
	const dLat = (bLat - aLat) * rad
	const dLon = (bLon - aLon) * rad
	const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLon / 2) ** 2
	return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** The other cameras around this one, nearest first */
function useNearbyWebcams(webcam: WebcamEntry): NearbyWebcam[] {
	const [nearby, setNearby] = useState<NearbyWebcam[]>([])
	useEffect(() => {
		let cancelled = false
		setNearby([])
		const dLon = NEARBY_DEG / Math.max(0.2, Math.cos(webcam.lat * Math.PI / 180))
		void WebcamStationsTable.queryByBBox(
			webcam.lon - dLon, webcam.lat - NEARBY_DEG, webcam.lon + dLon, webcam.lat + NEARBY_DEG,
			NEARBY_QUERY_LIMIT, false
		).then(rows => {
			if (cancelled) return
			setNearby(rows
				.filter(cam => cam.id !== webcam.id)
				.map(cam => ({ cam, distanceKm: distanceKm(webcam.lat, webcam.lon, cam.lat, cam.lon) }))
				.sort((a, b) => a.distanceKm - b.distanceKm))
		}).catch(() => { /* panel still works without the strip */ })
		return () => { cancelled = true }
	}, [webcam.id, webcam.lat, webcam.lon])
	return nearby
}

/**
 * Filmstrip of the nearest cameras: tap one to jump to it, arrows scroll
 * the strip, "Browse all" opens the whole neighborhood as a contact sheet.
 * This is the tap-friendly way into the sheet (clusters on the globe open
 * it with long-press / right-click).
 */
function NearbySection({ webcam }: { webcam: WebcamEntry }) {
	const dispatch = useAppDispatch()
	const nearby = useNearbyWebcams(webcam)
	const stripRef = useRef<HTMLDivElement>(null)
	if (nearby.length === 0) return null

	const strip = nearby.slice(0, NEARBY_STRIP_MAX)
	const scroll = (direction: 1 | -1) => {
		const el = stripRef.current
		if (el) el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" })
	}
	const jumpTo = (id: string) => {
		dispatch(sendDatasetAction({ type: "webcam", action: "selectId", id }))
	}
	const browseAll = () => {
		const farthest = nearby[nearby.length - 1]
		dispatch(setWebcamSheet({
			ids: [webcam.id, ...nearby.map(n => n.cam.id)],
			title: `${(nearby.length + 1).toLocaleString("en-US")} webcams near ${webcam.name}`,
			lat: webcam.lat,
			lon: webcam.lon,
			extentKm: Math.max(1, farthest.distanceKm),
			currentId: webcam.id,
		}))
	}

	return (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between gap-2">
				<SectionTitle accentClass="text-fuchsia-300">Nearby cameras</SectionTitle>
				<div className="flex items-center gap-0.5">
					<button
						type="button"
						onClick={() => scroll(-1)}
						aria-label="Scroll nearby cameras back"
						className="pointer-events-auto flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
					>
						<ChevronLeft className="h-3.5 w-3.5" aria-hidden />
					</button>
					<button
						type="button"
						onClick={() => scroll(1)}
						aria-label="Scroll nearby cameras forward"
						className="pointer-events-auto flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
					>
						<ChevronRight className="h-3.5 w-3.5" aria-hidden />
					</button>
					<button
						type="button"
						onClick={browseAll}
						className="pointer-events-auto ml-1 text-[11px] text-fuchsia-300 hover:text-white transition-colors"
					>
						Browse all {nearby.length + 1}
					</button>
				</div>
			</div>
			<div
				ref={stripRef}
				className="pointer-events-auto -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
			>
				{strip.map(({ cam, distanceKm: d }) => (
					<button
						key={cam.id}
						type="button"
						onClick={() => jumpTo(cam.id)}
						title={`${cam.name} · ${d < NEARBY_SAME_SITE_KM ? "same site" : formatDistance(d)}`}
						aria-label={`Jump to ${cam.name}, ${d < NEARBY_SAME_SITE_KM ? "same site" : `${formatDistance(d)} away`}`}
						className="relative h-9 w-16 shrink-0 overflow-hidden rounded-md border border-white/10 bg-slate-900 transition-colors hover:border-fuchsia-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400"
					>
						<img
							src={thumbUrlFor(cam)}
							alt=""
							loading="lazy"
							decoding="async"
							className={`absolute inset-0 h-full w-full object-cover ${cam.status === "stale" ? "grayscale opacity-60" : ""}`}
						/>
						{cam.stream_url && cam.status !== "stale" && (
							<span className="absolute left-1 top-1 h-1.5 w-1.5 rounded-full bg-red-500 ring-1 ring-black/60" />
						)}
					</button>
				))}
			</div>
		</div>
	)
}

interface WebcamBodyProps {
	webcam: WebcamEntry
	refreshNonce: number
	ctx: PanelShellContext
}

/**
 * Live view: the origin snapshot URL refreshed at the origin's cadence
 * (stable-URL sources update the image in place; a cache-busting query makes
 * the browser re-fetch it). Sources with per-capture URLs (e.g. FAA) fall
 * back to the CDN thumbnail, which the ingest refreshes on its schedule.
 * Cameras with a stream get a play button that swaps in the proper player
 * (YouTube embed / HLS / looping MP4 / MJPEG).
 */
function WebcamBody({ webcam, refreshNonce, ctx }: WebcamBodyProps) {
	const [tick, setTick] = useState(() => Date.now())
	const [failed, setFailed] = useState(false)
	const [playing, setPlaying] = useState(false)
	// Ultra-wide sources (NDBC BuoyCAM panoramas are 2880x300) render at their
	// natural aspect instead of being center-cropped into the 16:9 frame
	const [isPanorama, setIsPanorama] = useState(false)
	// When the origin snapshot last loaded successfully. This drives the
	// "Updated" label: the dataset's last_success is the server-side health
	// probe timestamp (hours old by design), not the age of the image the
	// panel is actually showing
	const [loadedAt, setLoadedAt] = useState<number | null>(null)

	const refreshMs = Math.max(MIN_REFRESH_MS, webcam.refresh_seconds * 1000)

	useEffect(() => {
		const interval = setInterval(() => setTick(Date.now()), refreshMs)
		return () => clearInterval(interval)
	}, [refreshMs])

	// Manual refresh (header button): bump the cache-bust tick and retry the
	// origin URL even if it previously failed (transient origin errors
	// shouldn't strand the panel on the CDN thumbnail until refocus)
	useEffect(() => {
		if (refreshNonce === 0) return
		setFailed(false)
		setTick(Date.now())
	}, [refreshNonce])

	const imageUrl = useMemo(() => {
		if (!failed && webcam.image_url) {
			const sep = webcam.image_url.includes("?") ? "&" : "?"
			return `${webcam.image_url}${sep}eb=${tick}`
		}
		// Fallback: CDN thumbnail, cache-busted by last ingest success so we
		// don't hammer B2 while still picking up newer thumbs on focus changes
		const stamp = webcam.last_success ? new Date(webcam.last_success).getTime() : 0
		return `${datasetUrl(webcam.thumb_url)}?eb=${stamp}`
	}, [webcam, tick, failed])

	const hasStream = !!webcam.stream_url && !!webcam.stream_type

	const isStale = webcam.status === "stale"
	const categoryLabel = categoryLabels[webcam.category] ?? "Webcam"

	return (
		<>
			<div className="space-y-1">
				<div className="text-[18px] leading-tight font-semibold text-white">
					{webcam.name}
				</div>
				<div className="flex items-center gap-2 text-xs">
					<Badge colorClass="bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30">
						{categoryLabel}
					</Badge>
					{isStale && (
						<Badge colorClass="bg-slate-500/20 text-slate-400 border border-slate-400/30">
							Stale
						</Badge>
					)}
					{webcam.region && <span className="text-slate-400">{webcam.region}</span>}
				</div>
			</div>

			<div className={`relative rounded-lg overflow-hidden border border-white/10 bg-slate-900/60 ${isPanorama && !playing ? "" : "aspect-video"}`}>
				{playing && hasStream ? (
					<>
						<StreamPlayer webcam={webcam} tick={tick} />
						<button
							onClick={() => setPlaying(false)}
							className="pointer-events-auto absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm text-slate-200 hover:text-white hover:bg-black/80 transition-colors"
							title="Back to snapshot"
						>
							<svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
								<rect x="0" y="0" width="8" height="8" rx="1" />
							</svg>
						</button>
					</>
				) : (
					<>
						<img
							src={imageUrl}
							alt={webcam.name}
							className={isPanorama ? "block w-full h-auto" : "absolute inset-0 w-full h-full object-cover"}
							onLoad={event => {
								const img = event.currentTarget
								setIsPanorama(img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight >= 3)
								if (!failed) setLoadedAt(Date.now())
							}}
							onError={() => setFailed(true)}
						/>
						{hasStream && (
							<button
								onClick={() => setPlaying(true)}
								className="pointer-events-auto absolute inset-0 flex items-center justify-center group"
								title="Play live stream"
							>
								<span className="w-12 h-12 flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm border border-white/20 group-hover:bg-black/75 group-hover:scale-105 transition-all">
									<svg width="18" height="18" viewBox="0 0 18 18" fill="white">
										<path d="M5 3.5v11l9-5.5z" />
									</svg>
								</span>
							</button>
						)}
					</>
				)}
				{!isStale && !playing && (
					<div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm pointer-events-none">
						<span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
						<span className="text-[10px] font-semibold uppercase tracking-wider text-slate-200">Live</span>
					</div>
				)}
			</div>

			<div className="flex items-center justify-between text-xs text-slate-400">
				<span>
					{playing
						? ""
						: failed
							? "Cached snapshot"
							: loadedAt
								? `Updated ${formatTimeAgo(new Date(loadedAt))}`
								: ""}
				</span>
				{webcam.attribution_url ? (
					<a
						href={webcam.attribution_url}
						target="_blank"
						rel="noopener noreferrer"
						className="pointer-events-auto text-slate-300 hover:text-white underline decoration-slate-600 underline-offset-2"
					>
						{webcam.attribution}
					</a>
				) : (
					<span className="text-slate-300">{webcam.attribution}</span>
				)}
			</div>

			<NearbySection webcam={webcam} />

			<MoreSection compact={ctx.compact}>
				<CoordFooter lat={webcam.lat} lon={webcam.lon} />
			</MoreSection>
		</>
	)
}
